// ===========================================================================
// quoteEngine — motor de cotización multimodal (módulo A, issue #52)
// ---------------------------------------------------------------------------
// Pure, framework-free pricing logic shared by the MultimodalQuoter UI. Keeping
// it side-effect free lets us unit-test every acceptance criterion (KG, FCL,
// LCL en todos los tamaños, todos los Incoterms, carga especial y la
// comparación de opciones/proveedores) without rendering React.
//
// The numbers are a transparent client-side estimate standing in for the
// provider API: PROVIDERS models the catalog of carriers/forwarders the real
// integration would return, so the comparison table reflects "datos del
// proveedor" once the API is wired in.
// ===========================================================================

export type Mode = "aerea" | "maritima" | "terrestre";
export type UnitType = "kg" | "fcl" | "lcl";
export type Incoterm = "EXW" | "FCA" | "FOB" | "CFR" | "CIF" | "DAP" | "DDP";
export type SpecialKey = "refrigerada" | "peligrosa" | "sobredimensionada" | "fragil";

export interface ModeConfig {
  value: Mode;
  label: string;
  icon: string;
  /** Base rate per chargeable kg (USD-equivalent, normalized units). */
  ratePerKg: number;
  /** Fixed handling/documentation base per shipment. */
  base: number;
}

export const MODES: ModeConfig[] = [
  { value: "aerea", label: "Aérea", icon: "ri-plane-line", ratePerKg: 3.8, base: 1200 },
  { value: "maritima", label: "Marítima", icon: "ri-ship-line", ratePerKg: 0.9, base: 800 },
  { value: "terrestre", label: "Terrestre internacional", icon: "ri-truck-line", ratePerKg: 1.4, base: 600 },
];

export interface ContainerConfig {
  value: string;
  label: string;
  price: number;
  /** True for reefer units — only reefer-capable providers can carry them. */
  reefer?: boolean;
}

export const CONTAINERS: ContainerConfig[] = [
  { value: "20gp", label: "20' GP (estándar)", price: 18500 },
  { value: "40gp", label: "40' GP (estándar)", price: 29800 },
  { value: "40hc", label: "40' HC (high cube)", price: 32400 },
  { value: "45hc", label: "45' HC", price: 36900 },
  { value: "20rf", label: "20' Reefer", price: 28900, reefer: true },
  { value: "40rf", label: "40' Reefer", price: 41200, reefer: true },
];

export const INCOTERMS: Incoterm[] = ["EXW", "FCA", "FOB", "CFR", "CIF", "DAP", "DDP"];

// Incoterm coverage multiplier: more seller responsibility → higher all-in cost.
export const INCOTERM_FACTOR: Record<Incoterm, number> = {
  EXW: 1.0, FCA: 1.04, FOB: 1.08, CFR: 1.16, CIF: 1.22, DAP: 1.3, DDP: 1.42,
};

export interface SpecialConfig {
  key: SpecialKey;
  label: string;
  surcharge: number;
  icon: string;
}

export const SPECIAL: SpecialConfig[] = [
  { key: "refrigerada", label: "Refrigerada", surcharge: 0.25, icon: "ri-temp-cold-line" },
  { key: "peligrosa", label: "Peligrosa (DG)", surcharge: 0.35, icon: "ri-alarm-warning-line" },
  { key: "sobredimensionada", label: "Sobredimensionada", surcharge: 0.3, icon: "ri-fullscreen-line" },
  { key: "fragil", label: "Frágil / alto valor", surcharge: 0.15, icon: "ri-shield-star-line" },
];

// ---------------------------------------------------------------------------
// Provider catalog — stands in for the carriers/forwarders the API will return.
// Each provider tweaks the base mode rate (rateFactor) and declares transit and
// the special cargo it can handle, so the comparison surfaces real trade-offs
// (precio vs. tránsito vs. capacidad para carga especial).
// ---------------------------------------------------------------------------
export interface Provider {
  id: string;
  name: string;
  mode: Mode;
  /** Multiplier applied to the computed base cost for this provider. */
  rateFactor: number;
  transitMinDays: number;
  transitMaxDays: number;
  /** 0–5 service reliability score, shown in the comparison. */
  rating: number;
  /** Special cargo types this provider can carry. */
  handles: SpecialKey[];
}

export const PROVIDERS: Provider[] = [
  { id: "aerocargo", name: "AeroCargo", mode: "aerea", rateFactor: 1.0, transitMinDays: 2, transitMaxDays: 4, rating: 4.6, handles: ["peligrosa", "fragil", "sobredimensionada"] },
  { id: "skyfreight", name: "SkyFreight", mode: "aerea", rateFactor: 0.92, transitMinDays: 3, transitMaxDays: 5, rating: 4.2, handles: ["fragil", "refrigerada", "peligrosa"] },
  { id: "oceanline", name: "OceanLine", mode: "maritima", rateFactor: 1.0, transitMinDays: 22, transitMaxDays: 35, rating: 4.4, handles: ["refrigerada", "sobredimensionada", "peligrosa", "fragil"] },
  { id: "eurofreight", name: "EuroFreight", mode: "maritima", rateFactor: 0.88, transitMinDays: 26, transitMaxDays: 40, rating: 4.0, handles: ["refrigerada", "fragil"] },
  { id: "transcontinental", name: "TransContinental", mode: "terrestre", rateFactor: 1.0, transitMinDays: 5, transitMaxDays: 9, rating: 4.3, handles: ["fragil", "sobredimensionada", "peligrosa"] },
];

export interface QuoteInput {
  mode: Mode;
  unit: UnitType;
  /** Actual weight in kg (used when unit === "kg"). */
  weight: number;
  /** Volume in m³ (used when unit === "kg" for the volumetric weight). */
  volume: number;
  /** Container size value (used when unit === "fcl" | "lcl"). */
  container: string;
  /** Fraction of the container used (0–1, used when unit === "lcl"). */
  lclFraction: number;
  incoterm: Incoterm;
  /** Selected special cargo keys. */
  special: SpecialKey[];
}

export interface QuoteBreakdown {
  basis: string;
  baseCost: number;
  incFactor: number;
  afterIncoterm: number;
  surcharges: SpecialConfig[];
  surchargeAmount: number;
  total: number;
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/**
 * Compute the cost breakdown for one cargo input against a base mode rate.
 * `rateFactor` lets a provider scale the base cost (defaults to 1 = list rate).
 */
export function computeQuote(input: QuoteInput, rateFactor = 1): QuoteBreakdown {
  const modeCfg = MODES.find((m) => m.value === input.mode) ?? MODES[0];

  let baseCost: number;
  let basis: string;

  if (input.unit === "kg") {
    // Chargeable weight = max(actual, volumetric). Volumetric ≈ vol(m³)*167 air.
    const w = Math.max(0, input.weight || 0);
    const v = Math.max(0, input.volume || 0);
    const volumetric = input.mode === "aerea" ? v * 167 : v * 333;
    const chargeable = Math.max(w, volumetric);
    baseCost = modeCfg.base + chargeable * modeCfg.ratePerKg;
    basis = `${chargeable.toFixed(0)} kg facturables (peso/volumen)`;
  } else if (input.unit === "fcl") {
    const c = CONTAINERS.find((x) => x.value === input.container) ?? CONTAINERS[0];
    baseCost = c.price;
    basis = `Contenedor completo ${c.label}`;
  } else {
    const c = CONTAINERS.find((x) => x.value === input.container) ?? CONTAINERS[0];
    const frac = clamp(input.lclFraction || 0.5, 0.05, 1);
    // LCL carries a consolidation premium vs the pro-rata FCL share.
    baseCost = c.price * frac * 1.35;
    basis = `LCL — ${Math.round(frac * 100)}% de ${c.label}`;
  }

  baseCost *= rateFactor;

  const incFactor = INCOTERM_FACTOR[input.incoterm];
  const surcharges = SPECIAL.filter((s) => input.special.includes(s.key));
  const surchargeRate = surcharges.reduce((acc, s) => acc + s.surcharge, 0);

  const afterIncoterm = baseCost * incFactor;
  const surchargeAmount = afterIncoterm * surchargeRate;
  const total = afterIncoterm + surchargeAmount;

  return { basis, baseCost, incFactor, afterIncoterm, surcharges, surchargeAmount, total };
}

export interface QuoteOption {
  provider: Provider;
  breakdown: QuoteBreakdown;
  total: number;
  transitLabel: string;
}

/**
 * Decide whether a provider can carry a given cargo input:
 *  - container units (FCL/LCL) are maritime-only,
 *  - reefer containers and "refrigerada" need a reefer-capable provider,
 *  - every selected special cargo type must be in the provider's `handles`.
 */
export function providerCanCarry(provider: Provider, input: QuoteInput): boolean {
  if ((input.unit === "fcl" || input.unit === "lcl") && provider.mode !== "maritima") {
    return false;
  }
  const container = CONTAINERS.find((c) => c.value === input.container);
  const needsReefer =
    input.special.includes("refrigerada") ||
    ((input.unit === "fcl" || input.unit === "lcl") && !!container?.reefer);
  if (needsReefer && !provider.handles.includes("refrigerada")) return false;

  return input.special.every((key) => provider.handles.includes(key));
}

/**
 * Build the comparison of every feasible provider/option for a cargo input,
 * sorted ascending by total price ("mejor precio" first). For KG the comparison
 * spans all modes; for FCL/LCL it is naturally limited to maritime providers.
 */
export function compareProviders(input: QuoteInput): QuoteOption[] {
  return PROVIDERS
    .filter((p) => providerCanCarry(p, input))
    .map((provider) => {
      // KG quotes use each provider's own mode; container quotes stay maritime.
      const scopedInput: QuoteInput =
        input.unit === "kg" ? { ...input, mode: provider.mode } : input;
      const breakdown = computeQuote(scopedInput, provider.rateFactor);
      return {
        provider,
        breakdown,
        total: breakdown.total,
        transitLabel: `${provider.transitMinDays}–${provider.transitMaxDays} días`,
      };
    })
    .sort((a, b) => a.total - b.total);
}
